import { getGlobalAuthContext } from "#root/components/auth";
import { z, type ZodType, type ZodUnknown } from "zod";
import {
  backendURL,
  processResponse,
  type RecordAddResponse,
  type RecordDeleteResponse,
  type RecordDetailResponse,
  type RecordUpdateResponse,
} from "./remote-data";

export type CollectionListResponse<RowType> = {
  success: true;
  foundRows: number;
  rows: RowType[];
  columns?: string[] | undefined;
};

export type CollectionSearchResponse<RowType> = {
  success: true;
  rows: RowType[];
  foundRows?: number;
};

export interface FetchOptions extends RequestInit {
  body?: any;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined>;
}

export interface ListQuery {
  offset?: number;
  limit?: number;

  [key: string]: any;
}

export default class RemoteCollection<ListRowType extends ZodType, DetailRowType extends (ZodType | undefined)> {
  private readonly collectionURL: URL;

  constructor(
    apiPath: string,
    public readonly listItemSchema: ListRowType,
    public readonly detailItemSchema?: DetailRowType
  ) {
    this.collectionURL = new URL(apiPath + "/", backendURL);
  }

  fetch(path: string, { body, headers = {}, query = {}, ...rest }: FetchOptions = {}): Promise<Response> {
    const url = new URL(path, this.collectionURL);
    for (const key in query) {
      const value = query[key];
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }

    const { token } = getGlobalAuthContext() ?? {};

    if (token) {
      headers.Authorization = "token " + token;
    }

    const init: RequestInit = { ...rest, headers };
    if (body) {
      headers["Content-Type"] = "application/json";
      init.body = JSON.stringify(body);
    }

    return fetch(url.toString(), init);
  }

  async list(query: ListQuery = {}) {
    if (query.offset == null) {
      query.offset = 0;
    }

    if (query.limit == null) {
      query.limit = 1000;
    }

    const response = await this.fetch("", {
      query,
      method: "GET",
    });

    const { rows, ...extra } = await processResponse<CollectionListResponse<ZodUnknown>>(response);

    return {
      ...extra,
      rows: rows.map((row, i) => this.listItemSchema.parse({...row, __idx: i}) as z.output<ListRowType>),
    } satisfies CollectionListResponse<z.output<ListRowType>>;
  }

  async search(record: any) {
    const response = await this.fetch("search", {
      method: "POST",
      body: record,
    });

    const { rows, ...extra } = await processResponse<CollectionSearchResponse<ZodUnknown>>(response);

    return {
      ...extra,
      ...(rows && {
        rows: rows.map((row, i) => this.listItemSchema.parse({...row, __idx: i}) as z.output<ListRowType>),
      }),
    } satisfies CollectionSearchResponse<z.output<ListRowType>>;
  }

  async details(id: string | number) {
    if (this.detailItemSchema == null) {
      throw new Error("Cannot fetch details for a collection without a detail schema");
    }

    const response = await this.fetch(String(id), {
      method: "GET",
    });

    const { row, ...extra } = await processResponse<RecordDetailResponse<{}>>(response);

    return {
      ...extra,
      ...(row && {
        row: this.detailItemSchema.parse(row) as z.output<DetailRowType>,
      }),
    } satisfies RecordDetailResponse<z.output<DetailRowType>>;
  }

  async add(record: any) {
    if (this.detailItemSchema == null) {
      throw new Error("Cannot add a record to a collection without a detail schema");
    }
    const response = await this.fetch("", {
      method: "POST",
      body: record,
    });

    const { row, ...extra } = await processResponse<RecordAddResponse<{}>>(response);

    return {
      ...extra,
      row: this.detailItemSchema.parse(row) as z.output<DetailRowType>,
    } satisfies RecordAddResponse<z.output<DetailRowType>>;
  }

  async delete(id: string | number) {
    if (this.detailItemSchema == null) {
      throw new Error("Cannot delete a record from a collection without a detail schema");
    }
    const response = await this.fetch(String(id), {
      method: "DELETE",
    });

    const { row, ...extra } = await processResponse<RecordDeleteResponse<{}>>(response);

    return {
      ...extra,
      ...(row && {
        row: this.detailItemSchema.parse(row) as z.output<DetailRowType>,
      }),
    } satisfies RecordDeleteResponse<z.output<DetailRowType>>;
  }

  async update(id: string | number, record: any) {
    if (this.detailItemSchema == null) {
      throw new Error("Cannot update a record in a collection without a detail schema");
    }
    const response = await this.fetch(String(id), {
      method: "PATCH",
      body: record,
    });

    const { row, ...extra } = await processResponse<RecordUpdateResponse<{}>>(response);

    return {
      ...extra,
      ...(row && {
        row: this.detailItemSchema.parse(row) as z.output<DetailRowType>,
      }),
    } satisfies RecordUpdateResponse<z.output<DetailRowType>>;
  }

  // Trigger a custom action - always POST
  async action<T = any>(name: string, record: any = {}) {
    const response = await this.fetch(name, {
      method: "POST",
      body: record,
    });

    return await processResponse<T>(response);
  }

  addOrUpdate(id: string | number | null | undefined, data: any) {
    if (id) {
      return this.update(id, data);
    }

    return this.add(data);
  }
}
